import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, RowInfo } from 'react-table';
import { Button, Table } from '@components/Common';
import CheckMark from '@components/Common/CheckMark';
import { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { HeadingRow, Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import AdminNavigation from '../AdminNavigation';
import TransportationCompanyEditor from './TransportationCompanyEditor';

// TanStack Query hooks
import {
  useTransportationCompaniesQuery,
  useCreateTransportationCompanyMutation,
  useUpdateTransportationCompanyMutation,
  TransportationCompany
} from '@api/queries/transportationCompanies';

const TransportationCompanies: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TransportationCompany | null>(null);

  // TanStack Query
  const { data: items = [], isLoading, refetch } = useTransportationCompaniesQuery();
  const createMutation = useCreateTransportationCompanyMutation();
  const updateMutation = useUpdateTransportationCompanyMutation();

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const columns: Column[] = [
    {
      Header: t('transportationCompanies.name'),
      id: 'name',
      accessor: 'name'
    },
    {
      Header: t('transportationCompanies.address'),
      id: 'address',
      accessor: 'address'
    },
    {
      Header: t('transportationCompanies.email'),
      id: 'email',
      accessor: 'email'
    },
    {
      Header: t('transportationCompanies.notes'),
      id: 'notes',
      accessor: 'notes'
    },
    {
      Header: t('transportationCompanies.vehicles'),
      id: 'vehicles',
      accessor: (row: TransportationCompany) => row.vehicles.map(v => v.register).join(', ')
    },
    {
      Header: t('transportationCompanies.drivers'),
      id: 'drivers',
      accessor: (row: TransportationCompany) => row.drivers.map(v => v.name).join(', ')
    },
    {
      Header: t('active'),
      id: 'isDeleted',
      width: 70,
      minWidth: 70,
      accessor: 'isDeleted',
      Cell: ({ value }) => <CheckMark active={!value} />,
      filterMethod: (filter, row) => (filter.value !== '' ? !row[filter.id] === (filter.value === 'true') : true),
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : ''}
        >
          <option value="" />
          <option value="true">{t('filter_active')}</option>
          <option value="false">{t('filter_inactive')}</option>
        </select>
      )
    }
  ];

  const formKey = (selectedItem && selectedItem.id) || 'new';

  const handleRowSelected = (rowInfo: RowInfo) => {
    setSelectedItem(rowInfo.original);
    setIsEditorOpen(true);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      <AdminNavigation isEnabled={!isEditorOpen} isLoading={isLoading} fetch={() => refetch()} />
      <HeadingRow>
        <Button primary={true} disabled={isSaving} onClick={handleAddClick}>
          {t('add_new')}
        </Button>
        {(isLoading || isSaving) && <OperationLoader title={t('updating') + '...'} />}
      </HeadingRow>
      <Layout>
        <MainContent>
          <ContentContainer>
            <Table
              data={items}
              columns={columns}
              filterable={true}
              defaultSortColumn="name"
              defaultSortDesc={true}
              defaultPageSize={20}
              noDataText={t('transportationCompanies.no_rows_companies')}
              onRowSelected={handleRowSelected}
              rowSelector={i => isEditorOpen && selectedItem && selectedItem.id === i.original.id}
              inactiveRowSelector={rowInfo => rowInfo.original.isDeleted}
            />
          </ContentContainer>
        </MainContent>
        {isEditorOpen && (
          <SideBar width={'750px'}>
            <TransportationCompanyEditor
              key={formKey}
              transportationCompanyToEdit={selectedItem}
              onClose={closeEditor}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default TransportationCompanies;
